import { create } from "zustand";
import type { WallOfLoveRequest, WallOfLoveState, WallOfLoveUpdateRequest, WallPreviewRequest } from "../types";
import { wallOfLoveApi } from "../services/wall-of-love-api";

export type WallPlanLimitErrorInfo = {
	feature: string;
	limit?: number;
	current_count?: number;
	message: string;
};

export class WallPlanLimitError extends Error {
	feature: string;
	limit?: number;
	current_count?: number;

	constructor(info: WallPlanLimitErrorInfo) {
		super(info.message);
		this.name = "WallPlanLimitError";
		this.feature = info.feature;
		this.limit = info.limit;
		this.current_count = info.current_count;
	}
}

export const useWallOfLoveStore = create<WallOfLoveState>((set) => ({
	wallOfLove: null,
	index: async (workspaceId: string) => {
		const response = await wallOfLoveApi.index(workspaceId);
		set({ wallOfLove: response.data });
		return response;
	},

	show: async (id: string, workspaceId: string) => {
		const response = await wallOfLoveApi.show(id, workspaceId);
		return response;
	},

	create: async (workspaceId: string, payload: WallOfLoveRequest) => {
		const response = await wallOfLoveApi.create(workspaceId, payload);
		set((state) => ({ wallOfLove: state.wallOfLove && response.data ? [...state.wallOfLove, response.data] : response.data ? [response.data] : state.wallOfLove ?? [] }));
		return response;
	},

	update: async (id: string, workspaceId: string, payload: WallOfLoveUpdateRequest) => {
		const response = await wallOfLoveApi.update(id, workspaceId, payload);
		return response;
	},

	delete: async (id: string, workspaceId: string) => {
		const response = await wallOfLoveApi.delete(id, workspaceId);
		set((state) => ({ wallOfLove: state.wallOfLove && response.data ? state.wallOfLove.filter((wallOfLove) => wallOfLove.id !== id) : response.data ? [response.data] : state.wallOfLove ?? [] }));
		return response;
	},

	restore: async (id: string, workspaceId: string) => {
		const response = await wallOfLoveApi.restore(id, workspaceId);
		set((state) => ({ wallOfLove: state.wallOfLove && response.data ? [...state.wallOfLove, response.data] : response.data ? [response.data] : state.wallOfLove ?? [] }));
		return response;
	},

	syncStaticReviews: async (workspaceId: string, wallId: string, reviewIds: string[]) => {
		const response = await wallOfLoveApi.syncStaticReviews(workspaceId, wallId, reviewIds);
		if (response.status === 402 && response.error?.error === "plan_upgrade_required") {
			throw new WallPlanLimitError({
				feature: response.error.feature,
				limit: response.error.limit,
				current_count: response.error.current_count,
				message: response.message || "Plan limit exceeded",
			});
		}
		return response;
	},

	syncPinnedReviews: async (workspaceId: string, wallId: string, reviewIds: string[]) => {
		const response = await wallOfLoveApi.syncPinnedReviews(workspaceId, wallId, reviewIds);
		if (response.status === 402 && response.error?.error === "plan_upgrade_required") {
			throw new WallPlanLimitError({
				feature: response.error.feature,
				limit: response.error.limit,
				current_count: response.error.current_count,
				message: response.message || "Plan limit exceeded",
			});
		}
		return response;
	},

	indexPinnedReviews: async (workspaceId: string, wallId: string) => {
		const response = await wallOfLoveApi.indexPinnedReviews(workspaceId, wallId);
		return response;
	},

	preview: async (workspaceId: string, payload: WallPreviewRequest, cursor?: string | null) => {
		const response = await wallOfLoveApi.preview(workspaceId, payload, cursor);
		return response;
	},
}));
